import { useInfiniteQuery, type InfiniteData, type QueryClient } from '@tanstack/react-query';

import { getProjectAuditLogs } from '../lib/api';
import type { AuditLogEntry, AuditLogQueryParams } from '../types/audit.types';
import type { PaginatedItems } from '../lib/api-client';

/**
 * Hook for fetching audit logs for a project with infinite loading
 */
export const useAuditLogs = (
  projectId: string,
  baseParams: Omit<AuditLogQueryParams, 'page'> = {},
  enabled: boolean = true
) => {
  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch } = useInfiniteQuery<
    PaginatedItems<AuditLogEntry>,
    Error,
    InfiniteData<PaginatedItems<AuditLogEntry>, number>,
    readonly [string, string, Omit<AuditLogQueryParams, 'page'>],
    number
  >({
    queryKey: ['audit-logs', projectId, baseParams],
    queryFn: ({ pageParam }) =>
      getProjectAuditLogs(projectId, {
        ...baseParams,
        page: pageParam,
        limit: baseParams.limit,
      }),
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      // Check if we have more pages available
      const nextPage = lastPageParam + 1;
      const hasMorePages = nextPage < lastPage.pagination.totalPages;

      return hasMorePages ? nextPage : undefined;
    },
    initialPageParam: 0, // API uses 0-based pagination
    enabled: enabled && !!projectId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  const loadMore = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const allAuditLogs = data?.pages.flatMap((pageData) => pageData.items) || [];

  return {
    auditLogs: allAuditLogs,
    isLoading,
    isFetchingMore: isFetchingNextPage,
    error,
    loadMore,
    hasMore: hasNextPage ?? false,
    refetch,
  };
};

/**
 * Invalidate all audit log queries for a project (all params)
 */
export function invalidateAuditLogsQuery(queryClient: QueryClient, projectId: string) {
  queryClient.invalidateQueries({ queryKey: ['audit-logs', projectId], exact: false });
}
